import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { cx } from '../../lib/cx';
import { useToast } from '../../context/ToastContext';

export const Spinner = ({ size = 16, label, className }) => (
  <span className={cx('inline-flex items-center', className)} role={label ? 'status' : undefined}>
    <Loader2 size={size} className="animate-spin" aria-hidden="true" />
    {label && <span className="sr-only">{label}</span>}
  </span>
);

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) throw new Error('copy failed');
};

/** Copies `text` to the clipboard; `label` names what is copied (for screen readers). */
export const CopyButton = ({ text, label, className, showLabel = false }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const timer = useRef(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await copyText(text);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast({ tone: 'error', title: 'Could not copy', description: 'Your browser blocked clipboard access.' });
    }
  };

  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
      title={copied ? 'Copied' : `Copy ${label}`}
      className={cx(
        'inline-flex shrink-0 items-center gap-1 rounded-md p-1 text-faint transition-colors hover:bg-raised hover:text-fg',
        copied && 'text-success hover:text-success',
        className,
      )}
    >
      <Icon size={14} aria-hidden="true" />
      {showLabel && <span className="text-xs">{copied ? 'Copied' : 'Copy'}</span>}
    </button>
  );
};

export const EmptyState = ({ icon: Icon, title, children, actions, className, headingLevel = 2 }) => {
  const Heading = `h${headingLevel}`;
  return (
    <div className={cx('flex flex-col items-center px-6 py-14 text-center', className)}>
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-line bg-sunken text-muted">
          <Icon size={22} aria-hidden="true" />
        </div>
      )}
      <Heading className="text-base font-semibold text-fg">{title}</Heading>
      {children && <div className="mt-1.5 max-w-md text-sm text-muted">{children}</div>}
      {actions && <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
};

export const Kbd = ({ children }) => (
  <kbd className="rounded border border-line-strong bg-sunken px-1.5 py-px font-sans text-[0.7rem] font-medium text-muted">
    {children}
  </kbd>
);

/** Inline banner for errors or notices inside a page. */
export const Notice = ({ tone = 'info', icon: Icon, title, children, action, className }) => {
  const tones = {
    info: 'border-info/30 bg-info/5 text-fg',
    warning: 'border-warning/30 bg-warning/5 text-fg',
    danger: 'border-danger/30 bg-danger/5 text-fg',
  };
  const iconTones = { info: 'text-info', warning: 'text-warning', danger: 'text-danger' };
  return (
    <div className={cx('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm', tones[tone], className)}>
      {Icon && <Icon size={18} className={cx('mt-0.5 shrink-0', iconTones[tone])} aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cx('text-muted', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};
